// ========================================================================
// FILE: src/pages/Mitra/InputPenjualan.jsx
// PENJELASAN: Halaman untuk mitra mencari buku, menambahkannya ke keranjang,
//             dan menyimpan pesanan.
// ========================================================================
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getAllBooks, addSale, getCustomersByMitra } from '../../supabase/database';
import { generateOrderNumber } from '../../utils/orderUtils';
import { FaPlusSquare, FaShoppingCart, FaTrash, FaUser, FaSearch, FaUserPlus, FaUpload, FaDownload, FaExclamationTriangle } from 'react-icons/fa';
import { getCategoryDiscountQuota } from '../../utils/categoryQuota';
import { Link, useNavigate } from 'react-router-dom';
import Spinner from '../../components/ui/Spinner';
import Papa from 'papaparse';
import useNavigationGuard from '../../hooks/useNavigationGuard';

const InputPenjualan = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [allBooks, setAllBooks] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [notes, setNotes] = useState('');
  // Diskon tambahan dihapus dari UI; selalu dianggap 0
  const [ongkir, setOngkir] = useState(0);
  const [previewOrderNumber, setPreviewOrderNumber] = useState('');
  
  // State untuk pelanggan
  const [customers, setCustomers] = useState([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // State untuk CSV import
  const fileInputRef = useRef(null);
  const [parsedCartData, setParsedCartData] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  
  // State untuk navigation guard
  const [hasUnsavedData, setHasUnsavedData] = useState(false);
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Navigation guard hook
  useNavigationGuard(hasUnsavedData, (attemptedPath) => {
    setPendingNavigation(attemptedPath);
    setShowNavigationModal(true);
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const bookListPromise = getAllBooks();
        const customerListPromise = currentUser?.uid ? getCustomersByMitra(currentUser.uid) : Promise.resolve([]);
        
        const [bookList, customerList] = await Promise.all([
          bookListPromise,
          customerListPromise
        ]);
        
        console.log('Books loaded:', bookList.length);
        console.log('Customers loaded:', customerList.length);
        
        setAllBooks(bookList);
        setCustomers(customerList);
      } catch (error) {
        console.error("Gagal mengambil data:", error);
        setError(`Gagal mengambil data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentUser?.uid]);

  // Generate preview nomor pesanan saat component dimuat
  useEffect(() => {
    if (cartItems.length > 0) {
      setPreviewOrderNumber(generateOrderNumber());
    } else {
      setPreviewOrderNumber('');
    }
  }, [cartItems.length]);

  // Track unsaved data
  useEffect(() => {
    const hasData = cartItems.length > 0 || selectedCustomer || notes.trim() || ongkir > 0;
    setHasUnsavedData(hasData);
  }, [cartItems, selectedCustomer, notes, ongkir]);


  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    setSelectedBook(null);

    if (value.length > 1) {
      const filteredBooks = allBooks.filter(book =>
        String(book.kode_buku).toLowerCase().includes(value.toLowerCase()) ||
        String(book.judul_buku).toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5);
      setSuggestions(filteredBooks);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (book) => {
    setSearchInput(book.kode_buku);
    setSelectedBook(book);
    setSuggestions([]);
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearchInput(value);
    setSelectedCustomer(null); // Clear selected customer when typing

    if (value.length > 1) {
      console.log('Searching for:', value);
      console.log('Available customers:', customers);
      
      const filteredCustomers = customers.filter(customer => {
        const nameMatch = customer.nama?.toLowerCase().includes(value.toLowerCase());
        const phoneMatch = customer.no_wa?.includes(value);
        const addressMatch = customer.alamat?.toLowerCase().includes(value.toLowerCase());
        
        console.log(`Customer ${customer.nama}: name=${nameMatch}, phone=${phoneMatch}, address=${addressMatch}`);
        
        return nameMatch || phoneMatch || addressMatch;
      }).slice(0, 5);
      
      console.log('Filtered customers:', filteredCustomers);
      setCustomerSuggestions(filteredCustomers);
    } else {
      setCustomerSuggestions([]);
    }
  };

  const handleCustomerSuggestionClick = (customer) => {
    setCustomerSearchInput(''); // Clear search input
    setSelectedCustomer(customer);
    setCustomerSuggestions([]); // Clear suggestions
  };

  const handleAddToCart = () => {
    if (!selectedBook) {
      setError("Pilih buku dari daftar saran terlebih dahulu.");
      return;
    };

    const existingItemIndex = cartItems.findIndex(item => item.kode_buku === selectedBook.kode_buku);

    if (existingItemIndex > -1) {
      const updatedCart = [...cartItems];
      updatedCart[existingItemIndex].kuantitas += quantity;
      setCartItems(updatedCart);
    } else {
      // Otomatis terapkan kuota diskon sesuai kategori buku
      const categoryQuota = getCategoryDiscountQuota(selectedBook.kategori);
      console.log('🔍 Debug Kategori:', {
        bukuKategori: selectedBook.kategori,
        kuotaDariMapping: categoryQuota,
        judulBuku: selectedBook.judul_buku
      });
      setCartItems(prev => [...prev, { ...selectedBook, kuantitas: quantity, diskonPerItem: categoryQuota }]);
    }

    setSelectedBook(null);
    setSearchInput('');
    setQuantity(1);
    setError('');
  };

  const handleRemoveFromCart = (kode_buku) => {
    setCartItems(prev => prev.filter(item => item.kode_buku !== kode_buku));
  };

  const handleDiscountChange = (kode_buku, diskonValue) => {
    const updatedCart = cartItems.map(item => {
      if (item.kode_buku !== kode_buku) return item;
      const kuota = getCategoryDiscountQuota(item.kategori);
      const maxAllowed = kuota > 0 ? kuota : 100;
      const val = Math.max(0, Math.min(maxAllowed, Number(diskonValue)));
      return { ...item, diskonPerItem: val };
    });
    setCartItems(updatedCart);
  };

  const handleQuantityChange = (kode_buku, newQuantity) => {
    const quantity = Math.max(1, Number(newQuantity) || 1);
    const updatedCart = cartItems.map(item => 
      item.kode_buku === kode_buku 
        ? { ...item, kuantitas: quantity }
        : item
    );
    setCartItems(updatedCart);
  };

  const calculateTotals = () => {
    const totalHarga = cartItems.reduce((acc, item) => {
      const hargaSetelahDiskonItem = item.harga_buku - (item.harga_buku * (item.diskonPerItem || 0) / 100);
      return acc + (hargaSetelahDiskonItem * item.kuantitas);
    }, 0);
    // Diskon tambahan dihapus; hargaSetelahDiskon sama dengan totalHarga
    const hargaSetelahDiskon = totalHarga;
    const ongkirNumber = Number(ongkir);
    const subtotal = hargaSetelahDiskon + ongkirNumber;
    // Total komisi mitra dihitung dari sisa kuota per item
    const totalKomisi = cartItems.reduce((acc, item) => {
      // Komisi mitra dihitung dari harga awal berdasarkan diskon yang DIBERIKAN
      const diskonPct = Math.max(0, Math.min(100, Number(item.diskonPerItem) || 0));
      const komisiItem = (item.harga_buku || 0) * (item.kuantitas || 0) * (diskonPct / 100);
      return acc + komisiItem;
    }, 0);
    return { totalHarga, hargaSetelahDiskon, subtotal, totalKomisi };
  };

  const { totalHarga, hargaSetelahDiskon, subtotal, totalKomisi } = calculateTotals();

  // CSV Import Functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError('');
    setParsedCartData([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: header => header.trim().toLowerCase().replace(/\s+/g, '_'),
      complete: (results) => {
        const requiredHeaders = ['kode_buku', 'qty'];
        const actualHeaders = results.meta.fields;
        const missingHeaders = requiredHeaders.filter(h => !actualHeaders.includes(h));

        if (missingHeaders.length > 0) {
          setError(`Header CSV tidak valid. Kolom yang diperlukan: ${requiredHeaders.join(', ')}`);
          return;
        }

        // Validate and process data
        const validData = results.data.filter(row => 
          row.kode_buku && row.qty && Number(row.qty) > 0
        ).map(row => ({
          kode_buku: String(row.kode_buku).trim(),
          qty: Number(row.qty)
        }));

        if (validData.length === 0) {
          setError('Tidak ada data valid untuk diimpor. Pastikan kolom kode_buku dan qty diisi dengan benar.');
          return;
        }

        setParsedCartData(validData);
      },
      error: (error) => {
        setError('Gagal membaca file CSV. Pastikan format file benar.');
        console.error('CSV Parse Error:', error);
      }
    });
    e.target.value = null;
  };

  const handleConfirmCartImport = () => {
    setIsImporting(true);
    setError('');

    let addedCount = 0;
    let notFoundCount = 0;
    const notFoundBooks = [];

    parsedCartData.forEach(({ kode_buku, qty }) => {
      const book = allBooks.find(b => String(b.kode_buku).toLowerCase() === kode_buku.toLowerCase());
      
      if (book) {
        const existingItemIndex = cartItems.findIndex(item => item.kode_buku === book.kode_buku);
        
        if (existingItemIndex > -1) {
          // Update existing item quantity
          const updatedCart = [...cartItems];
          updatedCart[existingItemIndex].kuantitas += qty;
          setCartItems(updatedCart);
        } else {
          // Add new item to cart dengan kuota diskon otomatis
          const categoryQuota = getCategoryDiscountQuota(book.kategori);
          setCartItems(prev => [...prev, { ...book, kuantitas: qty, diskonPerItem: categoryQuota }]);
        }
        addedCount++;
      } else {
        notFoundCount++;
        notFoundBooks.push(kode_buku);
      }
    });

    let successMessage = `${addedCount} produk berhasil ditambahkan ke keranjang.`;
    if (notFoundCount > 0) {
      setError(`${notFoundCount} kode buku tidak ditemukan: ${notFoundBooks.join(', ')}`);
    } else {
      setMessage(successMessage);
    }

    setParsedCartData([]);
    setIsImporting(false);
  };

  const downloadTemplate = () => {
    const csvContent = "kode_buku,qty\nBK001,2\nBK002,1\nBK003,5";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'template_keranjang_pesanan.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (cartItems.length === 0) {
      setError('Keranjang pesanan masih kosong.');
      return;
    }
    if (!selectedCustomer) {
      setError('Pilih pelanggan terlebih dahulu.');
      return;
    }
    if (!currentUser || !(currentUser.nama || currentUser.nama_mitra) || !currentUser.kode_mitra) {
        setError('Data profil mitra tidak lengkap. Silakan hubungi admin.');
        console.error("Data pengguna tidak lengkap:", currentUser);
        console.error("Missing fields - nama/nama_mitra:", !(currentUser?.nama || currentUser?.nama_mitra), "kode_mitra:", !currentUser?.kode_mitra);
        return;
    }

    setIsSubmitting(true);

    // Generate nomor pesanan otomatis
    const noPesanan = generateOrderNumber();

    const saleData = {
      noPesanan,
      mitraUid: currentUser.uid,
      mitraEmail: currentUser.email,
      nama_mitra: currentUser.nama || currentUser.nama_mitra,
      kode_mitra: currentUser.kode_mitra,
      // Gunakan komisi global tetap, tidak berdasarkan mitra
      persentase_komisi: 15,
      // Link to existing customer record
      customerId: selectedCustomer.id,
      // Keep backward-compatible fields for display/migration
      pelanggan: {
        nama_pelanggan: selectedCustomer.nama,
        no_wa: selectedCustomer.no_wa,
        alamat: selectedCustomer.alamat
      },
      notes,
      // Diskon tambahan dihapus; simpan 0 untuk kompatibilitas
      diskon: 0,
      ongkir: Number(ongkir),
      totalHarga,
      hargaSetelahDiskon,
      subtotal,
      status: 'menunggu validasi',
      pembayaran: 'belum dibayar', // Default payment status
      items: cartItems
    };

    try {
      await addSale(saleData);
      setMessage('Pesanan berhasil disimpan dan menunggu validasi.');
      setHasUnsavedData(false); // Clear unsaved data flag
      
      // Clear form data
      setCartItems([]);
      setSelectedCustomer(null);
      setCustomerSearchInput('');
      setNotes('');
      // Diskon tambahan dihapus, tidak perlu reset
      setOngkir(0);
      
      // Show success message briefly then redirect
      setTimeout(() => {
        navigate('/mitra/riwayat-pesanan');
      }, 1500); // Wait 1.5 seconds to show success message
      
    } catch (error) {
      setError('Gagal menyimpan pesanan. Periksa koneksi Anda dan coba lagi.');
      console.error("Error saat menyimpan pesanan:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Navigation confirmation handlers
  const handleConfirmNavigation = () => {
    setShowNavigationModal(false);
    if (pendingNavigation) {
      setHasUnsavedData(false); // Clear flag to allow navigation
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationModal(false);
    setPendingNavigation(null);
  };

  const handleSaveAndNavigate = async () => {
    if (cartItems.length === 0) {
      setError('Keranjang pesanan masih kosong.');
      return;
    }
    if (!selectedCustomer) {
      setError('Pilih pelanggan terlebih dahulu.');
      return;
    }
    if (!currentUser || !(currentUser.nama || currentUser.nama_mitra) || !currentUser.kode_mitra) {
        setError('Data profil mitra tidak lengkap. Silakan hubungi admin.');
        return;
    }

    setIsSubmitting(true);
    const noPesanan = generateOrderNumber();

    const saleData = {
      noPesanan,
      mitraUid: currentUser.uid,
      mitraEmail: currentUser.email,
      nama_mitra: currentUser.nama || currentUser.nama_mitra,
      kode_mitra: currentUser.kode_mitra,
      // Gunakan komisi global tetap, tidak berdasarkan mitra
      persentase_komisi: 15,
      // Link to existing customer record
      customerId: selectedCustomer.id,
      pelanggan: {
        nama_pelanggan: selectedCustomer.nama,
        no_wa: selectedCustomer.no_wa,
        alamat: selectedCustomer.alamat
      },
      notes,
      // Diskon tambahan dihapus; simpan 0 untuk kompatibilitas
      diskon: 0,
      ongkir: Number(ongkir),
      totalHarga,
      hargaSetelahDiskon,
      subtotal,
      status: 'menunggu validasi',
      pembayaran: 'belum dibayar',
      items: cartItems
    };

    try {
      await addSale(saleData);
      setHasUnsavedData(false); // Clear unsaved data flag
      setShowNavigationModal(false);
      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }
    } catch (error) {
      setError('Gagal menyimpan pesanan. Periksa koneksi Anda dan coba lagi.');
      console.error("Error saat menyimpan pesanan:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="p-4 md:p-8 bg-white min-h-full">
      <div className="flex items-center gap-4 mb-6">
        <FaPlusSquare className="text-4xl text-amber-500" />
        <h1 className="text-3xl font-bold text-gray-800">Input Pesanan Baru</h1>
      </div>

      {/* Form Pencarian Pelanggan + Panel Pelanggan Terpilih (Side by Side) */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Kolom kiri: Pilih Pelanggan */}
        <div className="bg-blue-50 p-6 rounded-lg shadow-sm">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaUser className="inline mr-2 text-gray-400" />
              Pilih Pelanggan *
            </label>
            <div className="relative">
              <input 
                type="text" 
                value={customerSearchInput} 
                onChange={handleCustomerSearchChange}
                className="w-full p-3 border rounded-md pr-10"
                placeholder={selectedCustomer ? "Ketik untuk mengganti pelanggan..." : "Cari nama, nomor WA, atau alamat pelanggan..."}
              />
              <FaSearch className="absolute right-3 top-3 text-gray-400" />
              {customerSuggestions.length > 0 && (
                <ul className="absolute z-10 w-full bg-white border rounded-md mt-1 shadow-lg max-h-60 overflow-y-auto">
                  {customerSuggestions.map(customer => (
                    <li
                      key={customer.id}
                      onClick={() => handleCustomerSuggestionClick(customer)}
                      className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                    >
                      <div className="font-semibold text-gray-900">{customer.nama}</div>
                      <div className="text-sm text-gray-600">{customer.no_wa}</div>
                      <div className="text-xs text-gray-500 truncate">{customer.alamat}</div>
                    </li>
                  ))}
                </ul>
              )}
              {customerSearchInput.length > 1 && customerSuggestions.length === 0 && (
                <div className="absolute z-10 w-full bg-white border rounded-md mt-1 shadow-lg p-3 text-center text-gray-500">
                  <p>Pelanggan tidak ditemukan</p>
                  <Link 
                    to="/mitra/daftar-pelanggan" 
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center justify-center mt-2"
                  >
                    <FaUserPlus className="mr-1" />
                    Tambah pelanggan baru
                  </Link>
                </div>
              )}
            </div>
            {customers.length === 0 && (
              <div className="mt-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800 mb-2">Anda belum memiliki data pelanggan.</p>
                <Link 
                  to="/mitra/daftar-pelanggan" 
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                >
                  <FaUserPlus className="mr-1" />
                  Kelola daftar pelanggan
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Kolom kanan: Pelanggan Terpilih */}
        <div className="bg-green-50 p-6 rounded-lg shadow-sm border border-green-200">
          <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center">
            <FaUser className="mr-2" />
            Pelanggan Terpilih
          </h3>
          {selectedCustomer ? (
            <div className="space-y-2 text-sm">
              <div className="font-semibold text-green-900 text-base">{selectedCustomer.nama}</div>
              <div className="text-green-800">WA: {selectedCustomer.no_wa}</div>
              <div className="text-green-700 text-sm">{selectedCustomer.alamat}</div>
              <button
                type="button"
                onClick={() => setSelectedCustomer(null)}
                className="mt-2 inline-block text-xs text-red-600 hover:text-red-700"
              >
                Ganti pelanggan
              </button>
            </div>
          ) : (
            <div className="text-sm text-green-700">
              Belum ada pelanggan terpilih. Silakan pilih pelanggan di kolom kiri.
            </div>
          )}
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-700 mb-4">Keranjang Pesanan</h2>
        
        {parsedCartData.length > 0 && (
          <div className="bg-blue-50 p-3 rounded-lg shadow-sm mb-4">
            <div className="p-4 bg-white rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-3">Pratinjau Data Import ({parsedCartData.length} item)</h4>
              <div className="overflow-x-auto max-h-40 border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="p-2 text-left font-medium text-blue-900">Kode Buku</th>
                      <th className="p-2 text-left font-medium text-blue-900">Qty</th>
                      <th className="p-2 text-left font-medium text-blue-900">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedCartData.slice(0, 5).map((row, i) => {
                      const book = allBooks.find(b => String(b.kode_buku).toLowerCase() === row.kode_buku.toLowerCase());
                      return (
                        <tr key={i} className="border-t">
                          <td className="p-2">{row.kode_buku}</td>
                          <td className="p-2">{row.qty}</td>
                          <td className="p-2">
                            {book ? (
                              <span className="text-green-600 text-xs font-medium">✓ Ditemukan: {book.judul_buku}</span>
                            ) : (
                              <span className="text-red-600 text-xs font-medium">✗ Tidak ditemukan</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {parsedCartData.length > 5 && (
                      <tr>
                        <td colSpan="3" className="p-2 text-center text-gray-500 text-xs">
                          ... dan {parsedCartData.length - 5} item lainnya
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex justify-end gap-3">
                <button 
                  onClick={() => setParsedCartData([])} 
                  className="text-gray-600 hover:text-gray-800 font-medium py-2 px-4 rounded-lg text-sm"
                >
                  Batal
                </button>
                <button 
                  onClick={handleConfirmCartImport} 
                  disabled={isImporting}
                  className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg text-sm transition-colors"
                >
                  {isImporting ? 'Mengimpor...' : `Tambah ke Keranjang (${parsedCartData.length} item)`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Form Pencarian Buku */}
        <div className="bg-gray-50 p-6 rounded-lg shadow-sm mb-4">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700">Cari Kode / Judul Buku</label>
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              className="mt-1 w-full p-2 border rounded-md"
              placeholder="Ketik untuk mencari..."
            />
            {suggestions.length > 0 && (
              <ul className="absolute z-10 w-full bg-white border rounded-md mt-1 shadow-lg max-h-60 overflow-y-auto">
                {suggestions.map(book => (
                  <li
                    key={book.kode_buku}
                    onClick={() => handleSuggestionClick(book)}
                    className="p-2 hover:bg-gray-100 cursor-pointer"
                  >
                    <span className="font-bold">{book.kode_buku}</span> - {book.judul_buku}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {selectedBook && (
            <div className="bg-blue-100 p-4 mt-4 rounded-md flex items-center justify-between">
              <div>
                <p className="font-bold">{selectedBook.judul_buku}</p>
                <p className="text-sm text-gray-600">Harga: Rp {selectedBook.harga_buku.toLocaleString('id-ID')}</p>
                <p className="text-xs text-blue-700 mt-1">
                  Kategori: {selectedBook.kategori || '-'}
                  {selectedBook.kategori && (
                    <> • Kuota diskon: {getCategoryDiscountQuota(selectedBook.kategori)}%</>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                  className="w-20 text-center p-1 border rounded-md"
                />
                <button onClick={handleAddToCart} className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-3 rounded-md">
                  Tambah
                </button>
              </div>
            </div>
          )}

          {/* CSV Import Section */}
          <div className="mt-4 flex items-center justify-between gap-3 p-3 bg-white border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-600 flex-1">
              💡 Import banyak buku sekaligus dengan file CSV
            </p>
            <div className="flex items-center gap-2">
              <input 
                type="file" 
                accept=".csv" 
                onChange={handleFileSelect} 
                className="hidden" 
                ref={fileInputRef} 
              />
              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                className="inline-flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white font-medium py-1.5 px-3 rounded-lg transition-colors text-xs whitespace-nowrap"
              >
                <FaUpload className="text-xs" /> Import CSV
              </button>
              <button
                type="button"
                onClick={downloadTemplate}
                className="inline-flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white font-medium py-1.5 px-3 rounded-lg transition-colors text-xs whitespace-nowrap"
              >
                <FaDownload className="text-xs" /> Template
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produk</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Harga</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Diskon (%)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Harga Setelah Diskon</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aksi</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cartItems.length > 0 ? cartItems.map(item => {
                const hargaSetelahDiskonItem = item.harga_buku - (item.harga_buku * (item.diskonPerItem || 0) / 100);
                const totalHargaItem = hargaSetelahDiskonItem * item.kuantitas;
                const kuota = getCategoryDiscountQuota(item.kategori);
                const komisiPct = Math.max(0, Math.min(kuota || 100, item.diskonPerItem || 0));
                return (
                  <tr key={item.kode_buku}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <div>{item.judul_buku}</div>
                      <div className="text-xs text-gray-500">
                        Kategori: {item.kategori || '-'}{kuota ? ` • Kuota: ${kuota}%` : ''}{` • Diskon Sekolah: ${komisiPct}%`}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      Rp {item.harga_buku.toLocaleString('id-ID')}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="number"
                        min="1"
                        value={item.kuantitas}
                        onChange={(e) => handleQuantityChange(item.kode_buku, e.target.value)}
                        className="w-16 text-center p-1 border rounded-md text-sm"
                        placeholder="1"
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex flex-col items-center">
                        <input
                          type="number"
                          min="0"
                          max={kuota || 100}
                          value={item.diskonPerItem || 0}
                          onChange={(e) => handleDiscountChange(item.kode_buku, e.target.value)}
                          className="w-20 text-center p-1 border rounded-md text-sm"
                          placeholder="0"
                        />
                        {kuota ? (
                          <div className="text-[10px] text-gray-500 mt-1">Kuota {kuota}% • Diskon Sekolah {komisiPct}%</div>
                        ) : (
                          <div className="text-[10px] text-gray-400 mt-1">Tanpa kuota</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div className={item.diskonPerItem > 0 ? "font-semibold text-green-600" : ""}>
                        Rp {hargaSetelahDiskonItem.toLocaleString('id-ID')}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-800">
                      Rp {totalHargaItem.toLocaleString('id-ID')}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button onClick={() => handleRemoveFromCart(item.kode_buku)} className="text-red-500 hover:text-red-700">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="7" className="text-center p-4 text-gray-500">Keranjang masih kosong.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <form onSubmit={handleSubmitOrder}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="font-bold text-lg mb-4">Informasi Tambahan</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Catatan untuk Pesanan</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Catatan khusus untuk pesanan ini..."
                  rows="3"
                  className="mt-1 w-full p-2 border rounded-md resize-none"
                />
              </div>
              {/* Diskon Tambahan (%) dihapus */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Ongkos Kirim (Rp)</label>
                <input type="number" value={ongkir} onChange={(e) => setOngkir(e.target.value)} className="mt-1 w-full p-2 border rounded-md"/>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 p-6 rounded-lg">
            <h3 className="font-bold text-lg mb-4">Ringkasan Pesanan</h3>
            <div className="space-y-3 text-gray-700">
              <div className="flex justify-between">
                <span>Total Harga (sudah termasuk diskon per item)</span>
                <span className="font-semibold">Rp {totalHarga.toLocaleString('id-ID')}</span>
              </div>
              {/* Baris Diskon Tambahan dihapus */}
              <div className="flex justify-between">
                <span>Ongkos Kirim</span>
                <span className="font-semibold">Rp {Number(ongkir).toLocaleString('id-ID')}</span>
              </div>
              <hr className="my-2"/>
              <div className="flex justify-between text-xl font-bold text-gray-900">
                <span>TOTAL KESELURUHAN</span>
                <span>Rp {subtotal.toLocaleString('id-ID')}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span>Total Komisi Mitra (estimasi)</span>
                <span className="font-semibold text-green-600">Rp {Math.round(totalKomisi).toLocaleString('id-ID')}</span>
              </div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-6 w-full flex justify-center items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-4 rounded-lg transition-colors disabled:bg-gray-400"
            >
              <FaShoppingCart />
              {isSubmitting ? 'Menyimpan Pesanan...' : 'Simpan Pesanan'}
            </button>
            {message && <p className="text-center text-sm mt-4 text-green-600">{message}</p>}
            {error && <p className="text-center text-sm mt-4 text-red-600">{error}</p>}
          </div>
        </div>
      </form>

      {/* Navigation Confirmation Modal */}
      {showNavigationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <FaExclamationTriangle className="text-yellow-500 text-2xl mr-3" />
                <h3 className="text-lg font-semibold text-gray-800">Data Belum Disimpan</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Anda memiliki data pesanan yang belum disimpan. Apa yang ingin Anda lakukan?
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleSaveAndNavigate}
                  disabled={isSubmitting}
                  className="w-full flex justify-center items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:bg-gray-400"
                >
                  <FaShoppingCart />
                  {isSubmitting ? 'Menyimpan...' : 'Simpan dan Lanjutkan'}
                </button>
                <button
                  onClick={handleConfirmNavigation}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  Buang Data dan Lanjutkan
                </button>
                <button
                  onClick={handleCancelNavigation}
                  className="w-full bg-gray-500 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  Batal
                </button>
              </div>
              {error && <p className="text-center text-sm mt-4 text-red-600">{error}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InputPenjualan;
